// Release plan. Releases are sets of backlog items; feedback linked to an item shows the release date as
// its estimate and is marked Done when the release ships. The hotfix box is always open: rolling it puts
// its items live quietly, and they are announced with the next release (migration 014). The page opens on the
// roadmap (Roadmap.tsx); the plan view has the hotfix box, every release and the rolled hotfixes.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Rocket, Undo2, Zap } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { Avatar, Banner, Button, Card, Dialog, Pill, ProgressBar, Select, Spinner, TextArea, TextField, cx, initialsOf, type PillTone } from '../../ui';
import { NewItemDialog } from '../backlog/NewItemDialog';
import { Roadmap } from './Roadmap';
import {
  canSchedule, hotfixName, isOpen, KIND_LABEL, KIND_TONE, PRIORITY_RANK, STATUS_LABEL as ITEM_STATUS, STATUSES as ITEM_STATUSES, type BacklogData, type Item, type ItemStatus,
} from '../backlog/types';
import { fmtCalendarDate } from './types';

type ReleaseStatus = 'planned' | 'in_progress' | 'shipped' | 'cancelled';
const RELEASE_LABEL: Record<ReleaseStatus, string> = { planned: 'Planned', in_progress: 'In progress', shipped: 'Shipped', cancelled: 'Cancelled' };
const RELEASE_TONE: Record<ReleaseStatus, PillTone> = { planned: 'purple', in_progress: 'info', shipped: 'ok', cancelled: 'neutral' };

type ReleaseSummary = {
  id: string; name: string; target_date: string | null; status: ReleaseStatus; shipped_at: string | null; item_count: number; items_done: number;
  bug_count: number; feature_count: number; improvement_count: number; task_count?: number; request_count: number; hotfix_item_count: number;
};
type Rolled = { id: string; name: string; shipped_at: string; deploy_note: string | null; announced_in_id: string | null; announced_in_name: string | null; item_count: number; told_early_count: number };
type ReleasesResponse = {
  releases: ReleaseSummary[];
  hotfix: { box: { id: string; name: string; items: Item[] } | null; suggested_name: string; announce_with: { id: string; name: string; target_date: string | null } | null; rolled: Rolled[] };
  unscheduled: { count: number; high_bugs: number; with_feedback: number };
};
type Release = {
  id: string; name: string; kind: 'release' | 'hotfix'; summary: string | null; release_notes: string | null; target_date: string | null; status: ReleaseStatus;
  shipped_at: string | null; deploy_note: string | null; announced_in_id: string | null; announced_in_name: string | null; created_by_name: string;
  /** from_hotfixes: what this release shows (the next release, or what a shipped one announced). ships_hotfixes: what shipping it now announces. */
  items: Item[]; from_hotfixes: Item[]; ships_hotfixes?: Item[]; announce_with: { id: string; name: string } | null;
  events: Array<{ id: string; actor_name: string | null; type: string; at: string }>;
};

const msg = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);
type View = 'roadmap' | 'plan';
const VIEW_KEY = 'upm:releases-view';
const savedView = (): View => { try { return localStorage.getItem(VIEW_KEY) === 'plan' ? 'plan' : 'roadmap'; } catch { return 'roadmap'; } };
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function ReleasesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<ReleasesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [picker, setPicker] = useState(false);
  const [rolling, setRolling] = useState(false);
  const [view, setView] = useState<View>(savedView);
  const [backlog, setBacklog] = useState<Item[] | null>(null);
  const load = useCallback(() => api.get<ReleasesResponse>('/api/admin/releases').then(setData, (e) => setError(msg(e, 'Could not load releases.'))), []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (view !== 'roadmap' || backlog) return;
    api.get<BacklogData>('/api/admin/backlog').then((d) => setBacklog(d.items), (e) => setError(msg(e, 'Could not load the backlog.')));
  }, [view, backlog]);
  const choose = (v: View) => { setView(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* remembered for this visit only */ } };
  const create = async () => {
    try { navigate(`/admin/releases/${(await api.post<{ release: Release }>('/api/admin/releases', { name: name.trim(), target_date: date || null })).release.id}`); }
    catch (e) { setError(msg(e, 'Could not create the release.')); }
  };
  const setItem = async (id: string, set: Record<string, unknown>) => {
    try { await api.patch(`/api/admin/backlog/${id}`, set); void load(); } catch (e) { setError(msg(e, 'That change could not be saved.')); }
  };
  if (!data) return error ? <Banner tone="error" role="alert">{error}</Banner> : <Spinner label="Loading releases" />;
  const box = data.hotfix.box;
  const boxDone = box?.items.filter((i) => i.status === 'done').length ?? 0;
  return (
    <div className="mx-auto flex w-full max-w-shell flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <h1 className="page-title">Releases</h1>
          <p className="m-0 text-[13px] text-muted">Hotfixes go live quietly. Firms hear about everything when the next release ships.</p>
        </div>
        <Button size="sm" onClick={() => setCreating((v) => !v)}><Plus aria-hidden size={16} /> New release</Button>
      </div>
      {error && <Banner tone="error" role="alert">{error}</Banner>}
      {notice && <Banner tone="success" role="status">{notice}</Banner>}
      {creating && (
        <Card className="flex flex-wrap items-end gap-3 p-4">
          <TextField label="New release" placeholder="Release 4.3" value={name} onChange={(e) => setName(e.target.value)} className="min-w-[220px] flex-1" autoFocus />
          <TextField label="Target date" optional type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <Button variant="primary" disabled={!name.trim()} onClick={() => void create()}>Create</Button>
        </Card>
      )}

      <div role="tablist" aria-label="Releases view" className="flex w-fit gap-1 rounded-[10px] border border-line bg-white p-1">
        {(['roadmap', 'plan'] as View[]).map((v) => (
          <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => choose(v)}
            className={cx('min-h-9 rounded-lg border-0 px-3.5 text-[13px] font-semibold', view === v ? 'bg-brand text-white' : 'bg-transparent text-ink-2 hover:bg-subtle')}>
            {v === 'roadmap' ? 'Roadmap' : 'Plan'}
          </button>
        ))}
      </div>

      {view === 'roadmap' && (backlog ? <Roadmap releases={data.releases} items={backlog} announceWith={data.hotfix.announce_with?.id ?? null} /> : <Spinner label="Loading the roadmap" />)}

      {view === 'plan' && <>
      {box && (
        <section aria-labelledby="hot-h" className="overflow-hidden rounded-card border-[1.5px] border-warn-line bg-warn-tint">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-warn-line/60 px-4 py-3">
            <Zap aria-hidden size={18} className="text-warn-fg" />
            <div className="flex flex-col leading-tight">
              <h2 id="hot-h" className="m-0 text-base font-bold">Next hotfix</h2>
              <span className="text-xs text-muted">Always open. Rolls as {data.hotfix.suggested_name}. Announced with {data.hotfix.announce_with ? <Link to={`/admin/releases/${data.hotfix.announce_with.id}`}>{data.hotfix.announce_with.name}</Link> : 'the next release'}.</span>
            </div>
            <Pill tone="warn" dot>Open · {plural(box.items.length, 'item')} · {boxDone} done</Pill>
            <span className="ml-auto flex gap-2">
              <Button size="sm" onClick={() => setPicker(true)}>+ Add from backlog</Button>
              <Button size="sm" variant="primary" disabled={!box.items.length} onClick={() => setRolling(true)} className="!bg-[#B5530F] hover:!bg-[#9A460C]">Roll hotfix…</Button>
            </span>
          </div>
          {box.items.length === 0 ? <p className="m-0 px-4 py-3 text-[13px] text-muted">Empty. Add items here from the backlog.</p> : (
            <ul className="m-0 list-none px-4 py-1">
              {box.items.map((i) => <ItemLine key={i.id} item={i} onStatus={(s) => void setItem(i.id, { status: s })} onRemove={() => void setItem(i.id, { release_id: null })} removeLabel="Take out" />)}
            </ul>
          )}
        </section>
      )}

      <div className="flex flex-wrap items-start gap-4">
        <section aria-labelledby="rel-h" className="flex min-w-0 flex-[999_1_560px] flex-col gap-3">
          <h2 id="rel-h" className="m-0 text-[15px] font-bold">Releases</h2>
          {data.unscheduled.count > 0 && (
            <Link to="/admin/backlog" className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card border border-dashed border-line-strong bg-white px-3.5 py-2.5 text-ink no-underline hover:border-brand">
              <span className="font-bold">{plural(data.unscheduled.count, 'backlog item')} {data.unscheduled.count === 1 ? 'isn’t' : 'aren’t'} scheduled yet</span>
              <span className="text-[13px] text-muted">{plural(data.unscheduled.high_bugs, 'high-priority bug')} · {data.unscheduled.with_feedback} linked to firm feedback</span>
              <span className="ml-auto text-[13px] font-semibold text-brand">Open the backlog →</span>
            </Link>
          )}
          {data.releases.length === 0 ? <Card className="px-6 py-10 text-center text-muted">No releases yet.</Card> : (
            <ul className="m-0 grid list-none gap-3 p-0 md:grid-cols-2 xl:grid-cols-3">
              {data.releases.map((r) => (
                <li key={r.id}>
                  <Link to={`/admin/releases/${r.id}`} className="block no-underline">
                    <Card className="flex flex-col gap-1.5 p-4 text-ink hover:border-brand">
                      <div className="flex items-start justify-between gap-2"><span className="font-bold">{r.name}</span><Pill tone={RELEASE_TONE[r.status]} dot>{RELEASE_LABEL[r.status]}</Pill></div>
                      <span className="text-xs text-muted">{r.shipped_at ? `Shipped ${fmtDateTime(r.shipped_at)}` : r.target_date ? `Target ${fmtCalendarDate(r.target_date)}` : 'No target date'}</span>
                      <ProgressBar pct={r.item_count ? Math.round((r.items_done / r.item_count) * 100) : 0} label={`${r.items_done} of ${r.item_count} items done`} />
                      <span className="text-xs">{r.items_done}/{r.item_count} done{r.bug_count ? <> · <span className="font-semibold text-bad">{plural(r.bug_count, 'bug')}</span></> : null}{r.feature_count ? ` · ${plural(r.feature_count, 'feature')}` : ''}{r.improvement_count ? ` · ${plural(r.improvement_count, 'improvement')}` : ''}{r.task_count ? ` · ${plural(r.task_count, 'task')}` : ''}</span>
                      {r.hotfix_item_count > 0 && <span className="text-xs font-semibold text-warn-fg"><Zap aria-hidden size={11} className="inline" /> {r.status === 'shipped' ? `Announced ${plural(r.hotfix_item_count, 'hotfix item')}` : `+ ${plural(r.hotfix_item_count, 'item')} already live from hotfixes`}</span>}
                      <span className="text-xs text-muted">{plural(r.request_count, 'firm request')}</span>
                    </Card>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section aria-labelledby="hist-h" className="min-w-0 flex-[1_1_320px] overflow-hidden rounded-card border border-line bg-white">
          <div className="flex items-baseline gap-2 border-b border-line-soft px-3.5 py-3"><h2 id="hist-h" className="m-0 text-[15px] font-bold">Rolled hotfixes</h2><span className="text-xs text-muted">newest first</span></div>
          {data.hotfix.rolled.length === 0 ? <p className="m-0 px-3.5 py-3 text-[13px] text-muted">None yet.</p> : (
            <ul className="m-0 list-none p-0">
              {data.hotfix.rolled.map((h) => (
                <li key={h.id} className="border-t border-line-soft first:border-t-0">
                  <Link to={`/admin/releases/${h.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3.5 py-2 text-[13px] text-ink no-underline hover:bg-row-hi">
                    <span className="font-bold"><Zap aria-hidden size={12} className="inline text-warn-fg" /> {hotfixName(h.name)}</span>
                    <span className="text-muted">{fmtDateTime(h.shipped_at)}</span><span>{plural(h.item_count, 'item')}</span>
                    <span className="ml-auto">{h.announced_in_name ? <Pill tone="ok">In {h.announced_in_name}</Pill> : h.told_early_count ? <Pill tone="warn">{h.told_early_count} told early</Pill> : <Pill tone="neutral">Not announced yet</Pill>}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <p className="m-0 border-t border-line-soft px-3.5 py-2.5 text-xs text-ink-2">Everything here is live. Unannounced items go into the next release’s notes, and the firms who asked are told then.</p>
        </section>
      </div>
      </>}

      {picker && box && <AddFromBacklog title="Add to the next hotfix" to={{ id: box.id, name: 'the next hotfix' }} onClose={() => setPicker(false)} onDone={(n) => { setPicker(false); setNotice(`${plural(n, 'item')} added to the next hotfix.`); void load(); }} />}
      {rolling && box && <RollDialog box={box} suggested={data.hotfix.suggested_name} announceWith={data.hotfix.announce_with?.name ?? 'the next release'} onClose={() => setRolling(false)}
        onRolled={(text) => { setRolling(false); setNotice(text); void load(); }} />}
    </div>
  );
}

function ItemLine({ item, onStatus, onRemove, removeLabel, extra }: { item: Item; onStatus?: (s: ItemStatus) => void; onRemove?: () => void; removeLabel?: string; extra?: React.ReactNode }) {
  return (
    <li className="flex flex-col gap-1 border-t border-line-soft py-2 first:border-t-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold tabular-nums text-brand">{item.reference}</span>
        <Pill tone={KIND_TONE[item.kind]}>{KIND_LABEL[item.kind]}</Pill>
        <Link to={`/admin/backlog/${item.id}`} className="min-w-[200px] flex-1 font-semibold text-ink no-underline hover:underline">{item.title}</Link>
        {extra}
        {onStatus ? (
          <Select aria-label={`Status of ${item.reference}`} small value={item.status} onChange={(e) => onStatus(e.target.value as ItemStatus)}>
            {ITEM_STATUSES.map((s) => <option key={s} value={s}>{ITEM_STATUS[s]}</option>)}
          </Select>
        ) : <Pill tone={item.status === 'done' ? 'ok' : 'neutral'} dot>{ITEM_STATUS[item.status]}</Pill>}
        {onRemove && <Button variant="link" size="sm" onClick={onRemove}><Undo2 aria-hidden size={13} /> {removeLabel ?? 'Back to backlog'}</Button>}
      </div>
      <span className="flex flex-wrap items-center gap-2 text-xs text-muted">
        {item.requests.length ? <>Covers {item.requests.map((r, n) => <span key={r.id}>{n > 0 && ', '}<Link to={`/admin/feedback/${r.id}`}>{r.reference}</Link> ({r.firm_name ?? r.firm_key})</span>)}</> : 'Team item'}
        {item.owner_name && <span className="inline-flex items-center gap-1">· <Avatar initials={initialsOf(item.owner_name)} size={18} tone="soft" /> {item.owner_name}</span>}
      </span>
    </li>
  );
}

/** Pick open backlog items and put them in a release or the hotfix box. */
function AddFromBacklog({ title, to, onClose, onDone }: { title: string; to: { id: string; name: string }; onClose: () => void; onDone: (n: number) => void }) {
  const [data, setData] = useState<BacklogData | null>(null);
  const [q, setQ] = useState('');
  const [chip, setChip] = useState<'all' | 'bugs' | 'high' | 'firms' | 'elsewhere'>('all');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.get<BacklogData>('/api/admin/backlog').then(setData, (e) => setError(msg(e, 'Could not load the backlog.'))); }, []);
  const pool = useMemo(() => (data?.items ?? []).filter((i) => isOpen(i) && canSchedule(i) && i.release_id !== to.id), [data, to.id]);
  const shown = pool.filter((i) => (chip === 'elsewhere' ? !!i.release_id : !i.release_id)
    && (chip !== 'bugs' || i.kind === 'bug_fix') && (chip !== 'high' || PRIORITY_RANK[i.priority] <= 1) && (chip !== 'firms' || i.requests.length > 0)
    && (!q.trim() || `${i.reference} ${i.title}`.toLowerCase().includes(q.trim().toLowerCase())))
    .sort((a, b) => a.sort_order - b.sort_order);
  const add = async () => {
    try { await api.post('/api/admin/backlog/bulk', { ids: [...picked], set: { release_id: to.id } }); onDone(picked.size); }
    catch (e) { setError(msg(e, 'Could not add the items.')); }
  };
  const chips: Array<[typeof chip, string, number]> = [
    ['all', 'Not scheduled', pool.filter((i) => !i.release_id).length], ['bugs', 'Bugs', pool.filter((i) => !i.release_id && i.kind === 'bug_fix').length],
    ['high', 'High priority', pool.filter((i) => !i.release_id && PRIORITY_RANK[i.priority] <= 1).length], ['firms', 'Has firm feedback', pool.filter((i) => !i.release_id && i.requests.length).length],
    ['elsewhere', 'In another release', pool.filter((i) => i.release_id).length],
  ];
  return (
    <Dialog open title={title} wide onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!picked.size} onClick={() => void add()}>Add {picked.size || ''} to {to.name}</Button></>}>
      {error && <Banner tone="error" role="alert">{error}</Banner>}
      <TextField label="Search" placeholder="BL- number or words" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="flex flex-wrap gap-1.5">
        {chips.map(([k, l, n]) => <button key={k} type="button" aria-pressed={chip === k} onClick={() => setChip(k)} className={cx('min-h-8 rounded-pill border px-3 text-xs font-semibold', chip === k ? 'border-brand bg-brand-soft text-brand' : 'border-line-btn bg-white text-ink')}>{l} {n}</button>)}
      </div>
      {!data ? <Spinner label="Loading the backlog" /> : shown.length === 0 ? <p className="m-0 text-sm text-muted">Nothing here.</p> : (
        <ul className="m-0 max-h-[46vh] list-none overflow-auto rounded-[10px] border border-line p-0">
          {shown.map((i) => (
            <li key={i.id} className="border-t border-line-soft first:border-t-0">
              <label className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-[13px] hover:bg-row-hi">
                <input type="checkbox" checked={picked.has(i.id)} onChange={() => setPicked((s) => { const n = new Set(s); if (n.has(i.id)) n.delete(i.id); else n.add(i.id); return n; })} className="h-4 w-4 accent-brand" />
                <span className="text-xs font-bold text-brand">{i.reference}</span>
                <span className="min-w-0 flex-1 truncate">{i.title}</span>
                {i.release_id && <span className="text-xs text-muted">from {i.release_kind === 'hotfix' ? 'next hotfix' : i.release_name}</span>}
                {i.requests.length > 0 && <span className="text-xs text-muted">FB {i.requests.length}</span>}
                <Pill tone={KIND_TONE[i.kind]}>{KIND_LABEL[i.kind]}</Pill>
              </label>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

function RollDialog({ box, suggested, announceWith, onClose, onRolled }: {
  box: { id: string; items: Item[] }; suggested: string; announceWith: string; onClose: () => void; onRolled: (text: string) => void;
}) {
  const [name, setName] = useState(suggested);
  const [picked, setPicked] = useState<Set<string>>(new Set(box.items.filter((i) => i.status === 'done').map((i) => i.id)));
  const [tell, setTell] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const flip = (set: Set<string>, id: string) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); return n; };
  const carried = box.items.filter((i) => !picked.has(i.id));
  const toldNow = box.items.filter((i) => picked.has(i.id) && tell.has(i.id));
  const roll = async () => {
    setBusy(true); setError(null);
    try {
      const res = await api.post<{ requests_done: number; carried: number }>('/api/admin/releases/hotfix/roll',
        { name: name.trim(), item_ids: [...picked], tell_now: [...tell].filter((id) => picked.has(id)), deploy_note: note.trim() || null });
      onRolled(`${name.trim()} rolled with ${plural(picked.size, 'item')}.${res.requests_done ? ` ${plural(res.requests_done, 'firm request')} closed now.` : ''}${res.carried ? ` ${plural(res.carried, 'item')} moved to the next hotfix.` : ''}`);
    } catch (e) { setError(msg(e, 'The hotfix could not be rolled.')); setBusy(false); }
  };
  return (
    <Dialog open title="Roll hotfix" wide onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !picked.size || !name.trim()} onClick={() => void roll()} className="!bg-[#B5530F]"><Zap aria-hidden size={15} /> {busy ? 'Rolling…' : `Roll ${name.trim() || 'hotfix'} with ${plural(picked.size, 'item')}`}</Button></>}>
      {error && <Banner tone="error" role="alert">{error}</Banner>}
      <TextField label="Hotfix name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} hint="Suggested from the last release. Change it if your version differs." />
      <p className="m-0 text-sm">The ticked items go live in this hotfix. Firms aren’t told yet: they’re added to the <b>{announceWith}</b> notes and announced when it ships.</p>
      <ul className="m-0 list-none rounded-[10px] border border-line p-0">
        {box.items.map((i) => {
          const on = picked.has(i.id);
          return (
            <li key={i.id} className={cx('flex flex-col gap-1 border-t border-line-soft px-3 py-2 first:border-t-0', !on && 'opacity-75')}>
              <label className="flex cursor-pointer flex-wrap items-center gap-2 text-[13px]">
                <input type="checkbox" checked={on} onChange={() => setPicked((s) => flip(s, i.id))} className="h-4 w-4 accent-brand" />
                <span className="text-xs font-bold text-brand">{i.reference}</span><Pill tone={KIND_TONE[i.kind]}>{KIND_LABEL[i.kind]}</Pill>
                <span className="min-w-[160px] flex-1 font-semibold">{i.title}</span>
                <Pill tone={i.status === 'done' ? 'ok' : 'info'} dot>{ITEM_STATUS[i.status]}</Pill>
              </label>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pl-6 text-xs text-muted">
                {i.requests.length ? <span>Covers {i.requests.map((r) => `${r.reference} (${r.firm_name ?? r.firm_key})`).join(', ')}</span> : <span>Team item · no firm to tell</span>}
                {on && i.requests.length > 0 && (
                  <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 font-semibold text-ink-2">
                    <input type="checkbox" role="switch" checked={tell.has(i.id)} onChange={() => setTell((s) => flip(s, i.id))} className="h-4 w-4 accent-[#B5530F]" />
                    Tell {i.requests.length === 1 ? (i.requests[0]!.firm_name ?? 'the firm') : 'the firms'} now
                  </label>
                )}
                {!on && <span>Stays in the box for the next hotfix.</span>}
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-col gap-1 rounded-xl bg-brand-tint px-3.5 py-3 text-[13px]">
        <b>What happens</b>
        <span>1. {name.trim() || 'The hotfix'} is recorded with {plural(picked.size, 'item')} and today’s date. They show as live.</span>
        <span>2. They appear in {announceWith} under “Already live from hotfixes”.</span>
        <span>3. {toldNow.length ? `${plural(toldNow.reduce((n, i) => n + i.requests.length, 0), 'firm request')} ${toldNow.length === 1 ? 'is' : 'are'} marked Done now and the firm is told. ` : ''}Other linked firms hear when {announceWith} ships.</span>
        <span>4. A new empty box opens for the next hotfix{carried.length ? `, and ${plural(carried.length, 'item')} ${carried.length === 1 ? 'moves' : 'move'} into it` : ''}.</span>
      </div>
      <TextField label="Deploy note" optional hint="Internal, for example the image tag." value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
    </Dialog>
  );
}

export function ReleaseDetailPage() {
  const { id = '' } = useParams();
  const [release, setRelease] = useState<Release | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [picker, setPicker] = useState(false);
  const [newItem, setNewItem] = useState(false);
  const [shipping, setShipping] = useState(false);
  const load = useCallback(async () => {
    try { setRelease((await api.get<{ release: Release }>(`/api/admin/releases/${id}`)).release); }
    catch (e) { setError(msg(e, 'Could not load the release.')); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);
  const call = async (path: string, method: 'post' | 'patch', body?: unknown) => {
    setError(null);
    try { const res = await api[method]<{ release: Release } & Record<string, number>>(`/api/admin/releases/${id}${path}`, body); setRelease(res.release); return res; }
    catch (e) { setError(msg(e, 'That change could not be saved.')); return null; }
  };
  const setItem = async (itemId: string, set: Record<string, unknown>) => {
    try { await api.patch(`/api/admin/backlog/${itemId}`, set); void load(); } catch (e) { setError(msg(e, 'That change could not be saved.')); }
  };
  if (!release) return error ? <Banner tone="error" role="alert">{error}</Banner> : <Spinner label="Loading release" />;
  if (release.kind === 'hotfix') return <HotfixDetail release={release} />;
  const closed = release.status === 'shipped' || release.status === 'cancelled';
  const done = release.items.filter((i) => i.status === 'done');
  const notDone = release.items.filter((i) => i.status !== 'done');
  const requests = (list: Item[]) => list.reduce((n, i) => n + i.requests.filter((r) => ['received', 'planned', 'in_progress'].includes(r.status)).length, 0);
  const shipsHotfixes = release.ships_hotfixes ?? release.from_hotfixes;
  const hotQuiet = shipsHotfixes.filter((i) => !i.told_early_at);
  // Shipping a release that isn't the next one still announces every rolled hotfix.
  const early = !release.from_hotfixes.length && shipsHotfixes.length > 0 && release.announce_with && release.announce_with.id !== release.id;
  const ship = async () => {
    const res = await call('/ship', 'post');
    setShipping(false);
    if (res) setNotice(`Shipped and announced. ${plural(res.requests_done ?? 0, 'firm request')} marked Done${res.returned_to_backlog ? `; ${plural(res.returned_to_backlog, 'unfinished item')} went back to the backlog` : ''}.`);
  };
  return (
    <div className="mx-auto flex w-full max-w-shell flex-col gap-4">
      <Link to="/admin/releases" className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-brand no-underline"><ArrowLeft aria-hidden size={16} /> Releases</Link>
      {error && <Banner tone="error" role="alert">{error}</Banner>}
      {notice && <Banner tone="success" role="status">{notice}</Banner>}
      <ReleaseHeader release={release} closed={closed} onSave={(c) => call('', 'patch', c)} onShip={() => setShipping(true)}
        summary={`${done.length} of ${release.items.length} items done${release.from_hotfixes.length ? ` · plus ${plural(release.from_hotfixes.length, 'item')} already live from hotfixes` : ''}`} />

      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-line-soft px-4 py-2.5">
          <h2 className="m-0 text-[15px] font-bold">In this release · {release.items.length}</h2>
          {!closed && <span className="ml-auto flex gap-2"><Button size="sm" variant="outline" onClick={() => setPicker(true)}>+ Add from backlog</Button><Button size="sm" onClick={() => setNewItem(true)}>New item</Button></span>}
        </div>
        {release.items.length === 0 ? <p className="m-0 px-4 py-4 text-[13px] text-muted">No items yet. Add them from the backlog.</p> : (
          <ul className="m-0 list-none px-4 py-1">
            {release.items.map((i) => <ItemLine key={i.id} item={i} onStatus={closed ? undefined : (s) => void setItem(i.id, { status: s })} onRemove={closed ? undefined : () => void setItem(i.id, { release_id: null })} />)}
          </ul>
        )}
        {release.from_hotfixes.length > 0 && (
          <div className="border-t border-warn-line/60 bg-warn-tint">
            <div className="flex flex-wrap items-baseline gap-2 px-4 pb-1 pt-2.5">
              <h2 className="m-0 text-[15px] font-bold"><Zap aria-hidden size={14} className="inline text-warn-fg" /> {closed ? 'Announced from hotfixes' : 'Already live from hotfixes'} · {release.from_hotfixes.length}</h2>
              <span className="text-xs text-muted">{closed ? 'Shipped earlier in hotfixes and announced with this release.' : 'Shipped quietly. Announced with this release.'}</span>
            </div>
            <ul className="m-0 list-none px-4 pb-2">
              {release.from_hotfixes.map((i) => (
                <ItemLine key={i.id} item={i} extra={<><Pill tone="warn"><Zap aria-hidden size={11} /> {hotfixName(i.release_name)}{i.release_shipped_at ? ` · ${fmtDateTime(i.release_shipped_at).split(',')[0]}` : ''}</Pill>{i.told_early_at && <span className="text-xs font-semibold text-warn-fg">told early</span>}</>} />
              ))}
            </ul>
          </div>
        )}
      </Card>

      <Card className="p-5 text-sm">
        <details>
          <summary className="cursor-pointer text-[15px] font-bold">Activity</summary>
          <ul className="m-0 mt-2 flex list-none flex-col gap-2 p-0">
            {release.events.map((e) => <li key={e.id}><span className="font-semibold">{e.actor_name ?? 'System'}</span> {e.type.replace(/_/g, ' ')} <span className="text-xs text-muted">· {fmtDateTime(e.at)}</span></li>)}
          </ul>
        </details>
      </Card>

      {picker && <AddFromBacklog title={`Add to ${release.name}`} to={{ id: release.id, name: release.name }} onClose={() => setPicker(false)} onDone={(n) => { setPicker(false); setNotice(`${plural(n, 'item')} added.`); void load(); }} />}
      {newItem && <NewItemDialog open defaults={{ release_id: release.id }} onClose={() => setNewItem(false)} onCreated={() => void load()} />}
      {shipping && (
        <Dialog open title={`Ship and announce ${release.name}`} onClose={() => setShipping(false)}
          footer={<><Button onClick={() => setShipping(false)}>Cancel</Button><Button variant="primary" onClick={() => void ship()}><Rocket aria-hidden size={16} /> Ship and announce</Button></>}>
          <ul className="m-0 flex list-disc flex-col gap-1.5 pl-5 text-sm">
            <li>{plural(done.length, 'done item')} ship{done.length === 1 ? 's' : ''}. {plural(requests(done), 'linked firm request')} {requests(done) === 1 ? 'is' : 'are'} marked Done and the firms are told.</li>
            {shipsHotfixes.length > 0 && <li className={early ? 'font-semibold text-warn-fg' : undefined}>{plural(shipsHotfixes.length, 'item')} already live from hotfixes {shipsHotfixes.length === 1 ? 'is' : 'are'} announced too{early ? `, because this release ships before ${release.announce_with!.name}` : ''}{requests(hotQuiet) ? `: ${plural(requests(hotQuiet), 'more firm request')} marked Done` : ''}. Firms told early aren’t told again.</li>}
            {notDone.length > 0 && <li className="font-semibold text-warn-fg">{plural(notDone.length, 'item')} {notDone.length === 1 ? 'isn’t' : 'aren’t'} done (including won’t do) and go{notDone.length === 1 ? 'es' : ''} back to the backlog, still linked, with their release estimate cleared: {notDone.map((i) => i.reference).join(', ')}.</li>}
            {release.release_notes ? <li>Your release notes are ready for a “What’s new” announcement.</li> : <li>No release notes yet. You can write them after shipping too.</li>}
          </ul>
        </Dialog>
      )}
    </div>
  );
}

function HotfixDetail({ release }: { release: Release }) {
  const open = release.status === 'in_progress';
  return (
    <div className="mx-auto flex w-full max-w-shell flex-col gap-4">
      <Link to="/admin/releases" className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-brand no-underline"><ArrowLeft aria-hidden size={16} /> Releases</Link>
      <Card className="flex flex-wrap items-center gap-x-4 gap-y-2 p-5">
        <div className="flex flex-col gap-0.5">
          <h1 className="m-0 text-xl font-bold"><Zap aria-hidden size={18} className="inline text-warn-fg" /> {open ? 'Next hotfix (open)' : hotfixName(release.name)}</h1>
          <span className="text-xs text-muted">
            {open ? `Goes live when you roll it from the Releases page. Announced with ${release.announce_with?.name ?? 'the next release'}.`
              : `Rolled ${release.shipped_at ? fmtDateTime(release.shipped_at) : ''}${release.deploy_note ? ` · ${release.deploy_note}` : ''} · ${release.announced_in_name ? `announced in ${release.announced_in_name}` : `to be announced with ${release.announce_with?.name ?? 'the next release'}`}`}
          </span>
        </div>
        <Pill tone={open ? 'warn' : release.announced_in_id ? 'ok' : 'neutral'} dot>{open ? 'Open' : release.announced_in_id ? 'Announced' : 'Live, not announced yet'}</Pill>
        {open && <Link to="/admin/releases" className="ml-auto text-sm font-semibold">Roll it from Releases →</Link>}
      </Card>
      <Card className="p-0">
        <h2 className="m-0 border-b border-line-soft px-4 py-2.5 text-[15px] font-bold">{open ? 'In the box' : 'Shipped in this hotfix'} · {release.items.length}</h2>
        <ul className="m-0 list-none px-4 py-1">{release.items.map((i) => <ItemLine key={i.id} item={i} extra={i.told_early_at ? <span className="text-xs font-semibold text-warn-fg">firms told when rolled</span> : undefined} />)}</ul>
      </Card>
    </div>
  );
}

function ReleaseHeader({ release, closed, onSave, onShip, summary }: { release: Release; closed: boolean; onSave: (c: Record<string, unknown>) => Promise<unknown>; onShip: () => void; summary: string }) {
  const [name, setName] = useState(release.name);
  const [date, setDate] = useState(release.target_date ?? '');
  const [plan, setPlan] = useState(release.summary ?? '');
  const [notes, setNotes] = useState(release.release_notes ?? '');
  useEffect(() => { setName(release.name); setDate(release.target_date ?? ''); setPlan(release.summary ?? ''); setNotes(release.release_notes ?? ''); }, [release]);
  const changes: Record<string, unknown> = {};
  if (name.trim() !== release.name) changes.name = name.trim();
  if ((date || null) !== (release.target_date ?? null)) changes.target_date = date || null;
  if ((plan.trim() || null) !== (release.summary ?? null)) changes.summary = plan.trim();
  if ((notes.trim() || null) !== (release.release_notes ?? null)) changes.release_notes = notes.trim();
  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-end gap-3">
        <TextField label="Release" value={name} disabled={closed} onChange={(e) => setName(e.target.value)} className="min-w-[220px] flex-1" />
        <Pill tone={RELEASE_TONE[release.status]} dot>{RELEASE_LABEL[release.status]}</Pill>
        {!closed && (
          <Select label="Status" small value={release.status} onChange={(e) => {
            if (e.target.value === 'cancelled' && !window.confirm(`Cancel ${release.name}? Its unfinished items go back to the backlog.`)) return;
            void onSave({ status: e.target.value });
          }}>
            <option value="planned">Planned</option><option value="in_progress">In progress</option><option value="cancelled">Cancel release</option>
          </Select>
        )}
        {!closed && <Button variant="primary" onClick={onShip}><Rocket aria-hidden size={16} /> Ship and announce…</Button>}
      </div>
      <span className="-mt-2 text-xs text-muted">{summary}</span>
      <div className="grid gap-4 md:grid-cols-[200px_minmax(0,1fr)_minmax(0,1fr)]">
        <TextField label="Target date" type="date" disabled={closed} value={date} onChange={(e) => setDate(e.target.value)} hint="Linked requests show this as their estimate." />
        <TextArea label="Planning notes (staff only)" optional disabled={closed} rows={3} value={plan} onChange={(e) => setPlan(e.target.value)} />
        <TextArea label={'Release notes (for a "What\'s new" announcement)'} optional rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={release.status === 'cancelled'} />
      </div>
      {Object.keys(changes).length > 0 && release.status !== 'cancelled' && (!closed || Object.keys(changes).every((k) => k === 'release_notes')) && (
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary" size="sm" onClick={() => void onSave(changes)}>Save</Button>
          {changes.target_date !== undefined && <span className="text-[13px] text-muted">Linked requests with a release estimate move to the new date; their people are told once.</span>}
        </div>
      )}
    </Card>
  );
}
